import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, Camera, Link2, SkipForward, Upload, X, Shield, Clock, MapPin, Target, ClipboardList, FileText, Zap, Lock, Search } from 'lucide-react';
import axios from 'axios';
import { SiteNav } from './SiteNav';
import { useAuth, formatApiError } from '../contexts/AuthContext';
import { QSPriceBadge } from './QSPriceBadge';
import { QSCallCTA } from './QSCallCTA';
import { ScopeAssessmentCard } from './ScopeAssessmentCard';
import { ScopeInclusionsPanel } from './ScopeInclusionsPanel';
import './IndicativeTool.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Postcode validation
const PC_RE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

// Scope items by refurb level.
// May-2026 — extended to include "freshen-up + kitchen / bathroom / both"
// so clients who are decorating AND swapping out key rooms have the right
// starting preset (previously they had to pick either the freshen-up or
// the room change, never both).
const SCOPE_PRESETS = {
  cosmetic:                    ['decoration', 'flooring'],
  kitchen_only:                ['kitchen'],
  bathroom_only:               ['bathroom'],
  kitchen_bathroom:            ['kitchen', 'bathroom'],
  cosmetic_kitchen:            ['decoration', 'flooring', 'kitchen'],
  cosmetic_bathroom:           ['decoration', 'flooring', 'bathroom'],
  cosmetic_kitchen_bathroom:   ['decoration', 'flooring', 'kitchen', 'bathroom'],
  medium:                      ['decoration', 'flooring', 'bathroom', 'kitchen', 'electrics', 'plumbing', 'walls'],
  full:                        ['decoration', 'flooring', 'bathroom', 'kitchen', 'electrics', 'plumbing', 'walls', 'ceilings'],
};

const ALL_SCOPE_ITEMS = [
  { id: 'decoration', label: 'Decoration throughout', sub: 'Painting, wallpaper, freshening up' },
  { id: 'flooring', label: 'Floor finishes', sub: 'Carpet, tiles, vinyl, laminate' },
  { id: 'bathroom', label: 'Bathroom refurbishment', sub: 'Full or partial bathroom upgrade' },
  { id: 'kitchen', label: 'Kitchen installation', sub: 'Units, worktops, appliances' },
  { id: 'electrics', label: 'Electrical works', sub: 'Rewire, new sockets, lighting' },
  { id: 'plumbing', label: 'Plumbing and heating', sub: 'Pipework, radiators, boiler' },
  { id: 'walls', label: 'Wall finishes', sub: 'Replastering, dry lining, tiling' },
  { id: 'ceilings', label: 'Ceiling finishes', sub: 'Replaster, board, paint' },
  { id: 'windows', label: 'Windows and doors', sub: 'Replacement or overhaul' },
];

const EXTRA_SCOPE_ITEMS = [
  { id: 'structural', label: 'Structural changes', sub: 'New walls, openings or layout changes' },
  { id: 'stairs', label: 'Stairs', sub: 'Balustrade, treads or full replacement' },
];

const WITTY_LINES = [
  'Putting on the hard hat...',
  'Checking behind the radiators...',
  'Measuring the skirting boards...',
  'Inspecting the artex...',
  'Looking for woodchip wallpaper...',
  'Assessing the avocado bathroom suite...',
  'Checking the consumer unit...',
  'Counting the coats of paint needed...',
];

export const IndicativeTool = () => {
  const navigate = useNavigate();
  const { user, register, login } = useAuth();
  const [screen, setScreen] = useState(0);

  // State
  const [postcode, setPostcode] = useState('');
  const [postcodeValid, setPostcodeValid] = useState(false);
  const [address, setAddress] = useState('');
  const [addressFull, setAddressFull] = useState('');
  const [addressSub, setAddressSub] = useState('');
  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [epcArea, setEpcArea] = useState(null);
  const [epcLoading, setEpcLoading] = useState(false);
  const [areaOverride, setAreaOverride] = useState('');

  const [propType, setPropType] = useState('');
  const [refurbLevel, setRefurbLevel] = useState('');
  const [region, setRegion] = useState('');
  const [locationFactor, setLocationFactor] = useState(1.0);

  const [photoMode, setPhotoMode] = useState(null);
  // analysisFailed — true when the photo Vision analyse returns nothing
  // useful or errors out. Surfaces a "Switch to manual scope" CTA on Screen 2.
  const [analysisFailed, setAnalysisFailed] = useState(false);
  // scopeMode — May-2026 user spec. Surface "Photos vs Manual" choice on
  // Screen 1 (was previously buried on Screen 2 as the path picker). When
  // 'photos' the Scale of Works list is hidden — photos drive the scope.
  // When 'manual' the user picks from the list and we skip the photo step.
  const [scopeMode, setScopeMode] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [listingUrl, setListingUrl] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState('');
  const [aiDetectedItems, setAiDetectedItems] = useState([]);
  const [wittyLine, setWittyLine] = useState(WITTY_LINES[0]);

  const [scopeItems, setScopeItems] = useState(new Set());
  const [showExtras, setShowExtras] = useState(false);

  const [costResult, setCostResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [checkoutLoading] = useState(false);

  // ── PHASE B: registration + revealed estimate state ──
  // A `revealed` project_id means the user has registered (or was already logged in)
  // and the estimate in State B is unlocked.
  const [revealedProjectId, setRevealedProjectId] = useState(null);
  const [revealedOrderRef, setRevealedOrderRef] = useState(null);
  // §7.6 — UK GDPR: marketing consent must default to UNTICKED (opt-in only).
  const [regForm, setRegForm] = useState({ email: '', password: '', marketing: false, terms: false });
  const [regErr, setRegErr] = useState('');
  const [regBusy, setRegBusy] = useState(false);
  // ── Inline sign-in toggle (Feb 2026 — user reported being kicked to
  // /dashboard when they tried to sign in from the indicative reveal
  // gate, losing their just-calculated estimate). When `signInMode` is
  // true the gate swaps the register form for a compact email+password
  // login form that, on success, auto-reveals the estimate in place.
  const [signInMode, setSignInMode] = useState(false);
  const [signInForm, setSignInForm] = useState({ email: '', password: '' });
  const [signInErr, setSignInErr] = useState('');
  const [signInBusy, setSignInBusy] = useState(false);

  // ── Postcode handling ──
  const handlePostcodeChange = (val) => {
    const upper = val.toUpperCase();
    setPostcode(upper);
    setPostcodeValid(PC_RE.test(upper));
    if (!PC_RE.test(upper)) {
      setAddressConfirmed(false);
      setAddress('');
    }
  };

  // ── Address simulation (in production: GetAddress.io via backend proxy) ──
  const [addressList, setAddressList] = useState([]);
  const [showAddressList, setShowAddressList] = useState(false);

  const lookupAddresses = useCallback(async () => {
    if (!postcodeValid) return;
    try {
      const res = await axios.get(`${API}/address/lookup?postcode=${encodeURIComponent(postcode)}`);
      if (res.data.found && res.data.addresses?.length) {
        setAddressList(res.data.addresses);
      } else {
        // Fallback
        setAddressList([{ line1: postcode, city: '', postcode: postcode, full: postcode }]);
      }
    } catch (err) {
      console.error('Address lookup failed:', err?.message);
      setAddressList([{ line1: postcode, city: '', postcode: postcode, full: postcode }]);
    }
    setShowAddressList(true);
  }, [postcode, postcodeValid]);

  const selectAddress = async (addr) => {
    setAddress(addr.line1);
    setAddressFull(addr.full);
    setAddressSub(`${addr.city ? addr.city + ', ' : ''}${addr.postcode}`);
    setAddressConfirmed(true);
    setShowAddressList(false);

    // Determine region + factor
    try {
      const res = await axios.post(`${API}/indicative/calculate`, {
        area_m2: 80, scope_level: 'full', postcode: postcode
      });
      setRegion(res.data.region);
      setLocationFactor(res.data.location_factor);
    } catch (err) { console.error('Region lookup fallback:', err?.message); }

    // Background EPC lookup
    setEpcLoading(true);
    try {
      const epcRes = await axios.get(`${API}/epc/lookup?postcode=${encodeURIComponent(postcode)}&address=${encodeURIComponent(addr.line1)}`);
      if (epcRes.data.found && epcRes.data.floor_area) {
        setEpcArea(epcRes.data.floor_area);
      }
    } catch (err) { console.error('EPC lookup not available:', err?.message); }
    setEpcLoading(false);
  };

  const resetAddress = () => {
    setAddressConfirmed(false);
    setAddress('');
    setAddressFull('');
    setEpcArea(null);
  };

  const applyAreaOverride = () => {
    const val = parseFloat(areaOverride);
    if (val && val >= 10 && val <= 2000) {
      setEpcArea(val);
      setAreaOverride('');
    }
  };

  // ── Photo handling ──
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setPhotos(prev => [...prev.slice(0, 5), { src: ev.target.result, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const analysePhotos = async () => {
    setAiLoading(true);
    setAnalysisFailed(false);
    // Rotate witty lines
    let lineIdx = 0;
    const interval = setInterval(() => {
      lineIdx = (lineIdx + 1) % WITTY_LINES.length;
      setWittyLine(WITTY_LINES[lineIdx]);
    }, 2500);

    try {
      const base64Photos = photos.map(p => {
        const b64 = p.src.split(',')[1] || p.src;
        return p.src; // Send full data URL
      });

      const res = await axios.post(`${API}/vision/analyse`, { photos: base64Photos });
      const items = res.data.detected_items || [];
      const summary = res.data.summary || '';
      // Treat empty / non-actionable analysis as a failure so the user is
      // routed back to the manual path instead of getting a vague "we tried
      // but couldn't really tell" experience (May-2026 user spec).
      if (!items.length && !summary) {
        setAnalysisFailed(true);
      }
      setAiSummary(summary);
      setAiDetectedItems(items);
    } catch (err) {
      setAnalysisFailed(true);
      setAiSummary('');
    }

    clearInterval(interval);
    setAiLoading(false);
  };

  // ── Scope handling ──
  const toggleScope = (id) => {
    setScopeItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Cost calculation ──
  const calculateCost = async () => {
    setCalculating(true);
    try {
      const area = epcArea || 80;
      const res = await axios.post(`${API}/indicative/calculate`, {
        area_m2: area,
        scope_level: refurbLevel,
        postcode: postcode,
        scope_items: Array.from(scopeItems),
      });
      setCostResult(res.data);
    } catch (err) {
      console.error('Calculation failed:', err);
    }
    setCalculating(false);
  };

  // ── PHASE B: Reveal the estimate ──
  // This runs either when a logged-in user lands on Screen 4, or after they register via State A.
  const revealEstimate = useCallback(async () => {
    if (!costResult) return;
    try {
      const res = await axios.post(`${API}/indicative/create-free`, {
        address: address,
        address_full: addressFull,
        postcode: postcode,
        scope_level: refurbLevel,
        scope_items: Array.from(scopeItems),
        output_range_low: costResult?.low || 0,
        output_range_high: costResult?.high || 0,
        location_factor: locationFactor,
        region: region,
        property_type: propType,
      });
      setRevealedProjectId(res.data.project_id);
      setRevealedOrderRef(res.data.order_ref);
    } catch (err) {
      console.error('Reveal estimate failed:', err);
    }
  }, [costResult, address, addressFull, postcode, refurbLevel, scopeItems, locationFactor, region, propType]);

  // Auto-reveal when a logged-in user lands on Screen 4 with a cost result
  React.useEffect(() => {
    if (screen === 4 && user && costResult && !revealedProjectId) {
      revealEstimate();
    }
  }, [screen, user, costResult, revealedProjectId, revealEstimate]);

  // Register new user via Screen 4 State A form, then immediately reveal
  const handleRegister = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setRegErr('');
    if (!regForm.email || !regForm.email.includes('@')) { setRegErr('Please enter a valid email'); return; }
    if ((regForm.password || '').length < 8) { setRegErr('Password must be at least 8 characters'); return; }
    if (!regForm.terms) { setRegErr('Please accept the Terms and Conditions to continue'); return; }
    setRegBusy(true);
    try {
      await register({
        email: regForm.email.trim().toLowerCase(),
        password: regForm.password,
        marketing_consent: !!regForm.marketing,
        terms_accepted: true,
      });
      // Then reveal — creates the indicative project tied to the now-authed user
      await revealEstimate();
    } catch (ex) {
      setRegErr(formatApiError(ex));
    } finally {
      setRegBusy(false);
    }
  };

  // Sign-in inline from the gate — returning users skip /dashboard and
  // see their estimate in place (Feb 2026 user-reported friction fix).
  const handleSignInInline = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSignInErr('');
    if (!signInForm.email || !signInForm.email.includes('@')) { setSignInErr('Please enter a valid email'); return; }
    if (!signInForm.password) { setSignInErr('Please enter your password'); return; }
    setSignInBusy(true);
    try {
      await login({
        email: signInForm.email.trim().toLowerCase(),
        password: signInForm.password,
      });
      // useEffect at line 295 auto-reveals once `user` populates + costResult is set.
      // Belt-and-braces: explicit reveal too in case the effect timing is off.
      await revealEstimate();
    } catch (ex) {
      setSignInErr(formatApiError(ex));
    } finally {
      setSignInBusy(false);
    }
  };

  // ── Navigation ──
  const goScreen = (n) => {
    setScreen(n);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goScreen1 = () => goScreen(1);
  const goScreen2 = () => goScreen(2);
  // §3.8 — "Confirm what's included" step removed per Apr 2026 spec.
  // Pre-populate scope from refurb level + photo detections, then skip straight to output.
  const goScreen3 = async () => {
    const preset = new Set(SCOPE_PRESETS[refurbLevel] || []);
    aiDetectedItems.forEach(item => preset.add(item));
    setScopeItems(preset);
    goScreen(4);
    await calculateCost();
  };
  const goScreen4 = async () => {
    goScreen(4);
    await calculateCost();
  };

  const formatK = (n) => {
    if (n >= 1000) return Math.round(n / 1000);
    return n;
  };

  return (
    <div className="it-app">
      {/* Shared site nav */}
      <SiteNav cta="home" />

      <div className="it-body">
      {/* Progress steps */}
      <div className="it-steps">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className={`it-step${i < screen ? ' done' : i === screen ? ' active' : ''}`} />
        ))}
      </div>

      {/* ── SCREEN 0: Address ── */}
      {screen === 0 && (
        <div className="it-screen" data-testid="screen-0">
          {/* What you'll get */}
          <div className="it-info-card">
            <div className="it-info-label">What you'll get</div>
            <div className="it-info-items">
              {[
                { icon: <Target className="w-5 h-5" />, title: 'A ballpark cost range', desc: "Not a quote — a well-informed starting point based on your property size, location and scope of works." },
                { icon: <MapPin className="w-5 h-5" />, title: 'Adjusted for your area', desc: "Contractor pricing varies significantly across the UK. We reflect current market conditions and location so your estimate is meaningful for where you are." },
                { icon: <Clock className="w-5 h-5" />, title: 'Ready in 2-3 minutes', desc: "No measuring, no account needed to start." },
              ].map(item => (
                <div key={item.title} className="it-info-item">
                  <div className="it-info-icon">{item.icon}</div>
                  <div>
                    <div className="it-info-item-title">{item.title}</div>
                    <div className="it-info-item-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* QS Value Strip */}
          <div className="it-qs-strip">
            <div className="it-qs-strip-icon"><Shield className="w-5 h-5 text-white" /></div>
            <div>
              <div className="it-qs-strip-title">QS-grade cost intelligence — without the QS fees</div>
              <div className="it-qs-strip-desc">A professional feasibility estimate costs £500-£1,500. For most residential refurbs that's hard to justify. We've made it free to start.</div>
            </div>
          </div>

          {/* Address card */}
          <div className="it-card" data-testid="address-card">
            <div className="it-card-hdr">
              <h2>What's the property address?</h2>
              <p>We'll verify the address and pull the project details together.</p>
            </div>
            <div className="it-card-body">
              <div className="it-field">
                <label>Postcode <span className="it-req">*</span></label>
                <div className="it-pc-wrap">
                  <input
                    type="text"
                    className={`it-input${!postcodeValid && postcode.length > 4 ? ' error' : ''}`}
                    placeholder="e.g. M21 9WQ"
                    value={postcode}
                    onChange={(e) => handlePostcodeChange(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && lookupAddresses()}
                    data-testid="postcode-input"
                  />
                  {postcodeValid && <div className="it-pc-tick"><Check className="w-4 h-4" /></div>}
                </div>
              </div>

              {/* Address list */}
              {postcodeValid && !addressConfirmed && (
                <div className="it-field">
                  <label>Select your property <span className="it-req">*</span></label>
                  {!showAddressList ? (
                    <button className="it-btn it-btn-navy" onClick={lookupAddresses} data-testid="find-address-btn">
                      Find address
                    </button>
                  ) : (
                    <div className="it-addr-list" data-testid="address-list">
                      {addressList.map((a, i) => (
                        <button key={a.full || i} className="it-addr-opt" onClick={() => selectAddress(a)} data-testid={`addr-opt-${i}`}>
                          <div>{a.line1}</div>
                          <div className="it-addr-opt-sub">{a.city ? `${a.city}, ` : ''}{a.postcode}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Address confirmed */}
              {addressConfirmed && (
                <div className="it-field">
                  <div className="it-addr-confirmed" data-testid="address-confirmed">
                    <div className="it-addr-confirmed-label">Property confirmed</div>
                    <div className="it-addr-confirmed-value">{address}</div>
                    <div className="it-addr-confirmed-sub">{addressSub}</div>
                    <button className="it-link-btn" onClick={resetAddress}>Change address</button>
                  </div>

                  {/* EPC Size Card */}
                  {epcArea && (
                    <div className="it-epc-card" data-testid="epc-card">
                      <div className="it-epc-msg">
                        We think this property is around <strong>{epcArea}m²</strong> — we'll use this as the basis for your cost estimate. If that sounds off, update it below.
                      </div>
                      <div className="it-epc-override">
                        <span>Looks wrong?</span>
                        <input
                          type="text"
                          className="it-input-sm"
                          placeholder="Enter m²"
                          value={areaOverride}
                          onChange={(e) => setAreaOverride(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && applyAreaOverride()}
                          data-testid="area-override-input"
                        />
                        <button className="it-btn-sm" onClick={applyAreaOverride}>Update</button>
                      </div>
                    </div>
                  )}

                  {!epcArea && !epcLoading && (
                    <div className="it-epc-card">
                      <div className="it-epc-msg">
                        Enter the approximate floor area for your property.
                      </div>
                      <div className="it-epc-override">
                        <input
                          type="text"
                          className="it-input-sm"
                          placeholder="Floor area in m²"
                          value={areaOverride}
                          onChange={(e) => setAreaOverride(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && applyAreaOverride()}
                          data-testid="area-manual-input"
                        />
                        <button className="it-btn-sm" onClick={applyAreaOverride}>Set</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {addressConfirmed && (epcArea || false) && (
                <button className="it-btn it-btn-navy" onClick={goScreen1} data-testid="continue-0-btn">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              )}

              <div className="it-trust-row">
                <div className="it-trust-item">
                  <Shield className="w-3 h-3" /> No account needed to start
                </div>
                <div className="it-trust-item">
                  <Clock className="w-3 h-3" /> Takes 2-3 minutes
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SCREEN 1: Property type + scale ── */}
      {screen === 1 && (
        <div className="it-screen" data-testid="screen-1">
          <div className="it-card">
            <div className="it-card-hdr">
              <h2>About the project</h2>
              <p>Tell us the property type and scale of works.</p>
            </div>
            <div className="it-card-body">
              <div className="it-field">
                <label>Property type</label>
                <div className="it-sel-row">
                  {[
                    { id: 'house', label: 'House', icon: '🏠' },
                    { id: 'flat', label: 'Flat', icon: '🏢' },
                    { id: 'bungalow', label: 'Bungalow', icon: '🏡' },
                  ].map(pt => (
                    <button
                      key={pt.id}
                      className={`it-sel-btn${propType === pt.id ? ' sel' : ''}`}
                      onClick={() => setPropType(pt.id)}
                      data-testid={`proptype-${pt.id}`}
                    >
                      <span className="it-sel-icon">{pt.icon}</span>
                      {pt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="it-field">
                <label>How would you like to define the scope?</label>
                <div className="it-path-picker" data-testid="scope-mode-picker"
                  style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}
                >
                  <button
                    type="button"
                    className={`it-path-card${scopeMode === 'photos' ? ' it-path-active' : ''}`}
                    onClick={() => setScopeMode('photos')}
                    data-testid="scope-mode-photos"
                    style={{
                      textAlign: 'left',
                      padding: '16px 18px',
                      borderRadius: 12,
                      border: scopeMode === 'photos' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                      background: scopeMode === 'photos' ? '#EFF6FF' : '#fff',
                      cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: scopeMode === 'photos' ? '0 4px 14px rgba(37,99,235,.18)' : '0 1px 2px rgba(0,0,0,.04)',
                      transition: 'all .18s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <Camera className="w-4 h-4" style={{ color: '#2563EB' }} />
                      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: '#2563EB', textTransform: 'uppercase' }}>SCOPEvision</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2742', marginBottom: 3 }}>Upload property photos</div>
                    <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>
                      SCOPEvision reviews your photos to identify the works required. If your photos can&rsquo;t be read clearly we&rsquo;ll fall back to the manual options.
                    </div>
                  </button>
                  <button
                    type="button"
                    className={`it-path-card${scopeMode === 'manual' ? ' it-path-active' : ''}`}
                    onClick={() => setScopeMode('manual')}
                    data-testid="scope-mode-manual"
                    style={{
                      textAlign: 'left',
                      padding: '16px 18px',
                      borderRadius: 12,
                      border: scopeMode === 'manual' ? '2px solid #2563EB' : '1.5px solid #CBD5E1',
                      background: scopeMode === 'manual' ? '#EFF6FF' : '#fff',
                      cursor: 'pointer', fontFamily: 'inherit',
                      boxShadow: scopeMode === 'manual' ? '0 4px 14px rgba(37,99,235,.18)' : '0 1px 2px rgba(0,0,0,.04)',
                      transition: 'all .18s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <Zap className="w-4 h-4" style={{ color: '#2563EB' }} />
                      <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: '#2563EB', textTransform: 'uppercase' }}>Manual</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#0F2742', marginBottom: 3 }}>Pick from options</div>
                    <div style={{ fontSize: 12, color: '#64748B', lineHeight: 1.5 }}>
                      Choose the works that best reflect what you want done &mdash; kitchen, bathroom, full refurb, etc.
                    </div>
                  </button>
                </div>
              </div>

              {/* Scale of Works list — only appears when "manual" path chosen.
                  When "photos" path is chosen SCOPEvision determines the scope
                  from uploaded photos so this list is unnecessary noise. */}
              {scopeMode === 'manual' && (
              <div className="it-field" data-testid="scale-of-works-block">
                <label>Scale of works</label>
                <div className="it-scale-options">
                  {[
                    { id: 'cosmetic',                  icon: '🎨',  title: 'Freshen-up only',              sub: 'Decoration & flooring — no major works' },
                    { id: 'kitchen_only',              icon: '🍳',  title: 'New kitchen only',             sub: 'New kitchen — keep the rest of the house' },
                    { id: 'bathroom_only',             icon: '🛁',  title: 'New bathroom only',            sub: 'New bathroom suite — keep the rest of the house' },
                    { id: 'kitchen_bathroom',          icon: '🔧',  title: 'New kitchen & bathroom',       sub: 'Upgrading both key rooms' },
                    { id: 'cosmetic_kitchen',          icon: '🎨',  title: 'Freshen-up + new kitchen',     sub: 'Decoration, flooring & new kitchen' },
                    { id: 'cosmetic_bathroom',         icon: '🎨',  title: 'Freshen-up + new bathroom',    sub: 'Decoration, flooring & new bathroom suite' },
                    { id: 'cosmetic_kitchen_bathroom', icon: '✨',  title: 'Freshen-up + kitchen & bath',  sub: 'Decoration, flooring, new kitchen and bathroom' },
                    { id: 'full',                      icon: '🏠',  title: 'Full refurbishment',           sub: 'Taking it back and starting again — within the existing structure' },
                  ].map(rl => (
                    <button
                      key={rl.id}
                      className={`it-scale-btn${refurbLevel === rl.id ? ' sel' : ''}`}
                      onClick={() => setRefurbLevel(rl.id)}
                      data-testid={`refurb-${rl.id}`}
                    >
                      <span className="it-scale-icon">{rl.icon}</span>
                      <div>
                        <div className="it-scale-title">{rl.title}</div>
                        <div className="it-scale-sub">{rl.sub}</div>
                      </div>
                    </button>
                  ))}
                  <button
                    className="it-scale-btn complex"
                    onClick={() => goScreen('managed')}
                    data-testid="refurb-complex"
                  >
                    <span className="it-scale-icon">🏗️</span>
                    <div>
                      <div className="it-scale-title">Extension, loft, conversion or larger project</div>
                      <div className="it-scale-sub">New build, HMO, listed building, structural changes beyond an opening</div>
                    </div>
                  </button>
                </div>
                <div className="it-notice-sm">
                  This tool covers straightforward residential refurbishment only. Extensions, conversions, new builds and listed buildings require professional QS input.
                </div>
              </div>
              )}

              {/* Inline hint shows exactly what's still needed so users on
                  small screens don't get stuck at a greyed-out Continue
                  button (May-2026 user report — iPad Safari). */}
              {(() => {
                const missing = [];
                if (!propType) missing.push('property type');
                if (!scopeMode) missing.push('how to define the scope');
                if (scopeMode === 'manual' && !refurbLevel) missing.push('scale of works');
                if (!missing.length) return null;
                return (
                  <div data-testid="screen1-missing-hint"
                    style={{
                      background: '#FEF3C7', border: '1px solid #FDE68A',
                      color: '#92400E', fontSize: 12.5, lineHeight: 1.5,
                      padding: '10px 14px', borderRadius: 8,
                      marginBottom: 12, textAlign: 'center', fontWeight: 600,
                    }}>
                    Choose {missing.join(' &amp; ')} to continue.
                  </div>
                );
              })()}

              <div className="it-nav-btns">
                <button className="it-btn it-btn-ghost" onClick={() => goScreen(0)}>
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  className="it-btn it-btn-navy"
                  onClick={() => {
                    // Defensive fallbacks — if scopeMode somehow didn't stick
                    // but the user has picked a refurbLevel, treat as manual.
                    let mode = scopeMode;
                    if (!mode && refurbLevel) mode = 'manual';
                    if (mode === 'photos') {
                      // Photos path → Screen 2 with photo upload primed
                      setPhotoMode('upload');
                      goScreen2();
                    } else if (mode === 'manual' && refurbLevel) {
                      // Manual path → straight to results using the chosen
                      // refurbLevel as the scope. goScreen3() pre-populates
                      // scopeItems from SCOPE_PRESETS, navigates to Screen 4
                      // and triggers calculateCost(). This is the fix for the
                      // May-2026 "Continue does nothing in manual mode" bug —
                      // we used to call analysePhotos() with an empty photos
                      // array, which left the user staring at Screen 1 while
                      // the loading state ran invisibly.
                      setPhotoMode('skip');
                      goScreen3();
                    }
                  }}
                  disabled={
                    !propType ||
                    (!scopeMode && !refurbLevel) ||
                    (scopeMode === 'manual' && !refurbLevel)
                  }
                  data-testid="continue-1-btn"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SCREEN 2: Photos ── */}
      {screen === 2 && (
        <div className="it-screen" data-testid="screen-2">
          <div className="it-card">
            <div className="it-card-hdr">
              <h2>Upload property photos</h2>
              <p>3-6 photos work best — ideally one per room plus the kitchen and bathroom. Listing photos work too.</p>
            </div>
            <div className="it-card-body">
              {/* Photo upload only — the photo-vs-manual choice was moved to
                  Screen 1 (May-2026 user spec). If analysis fails we surface
                  a fallback CTA below to send the user back to manual mode. */}
              

              {/* §3.10 — Rightmove/Zoopla URL paste option removed per Apr 2026 spec. */}
              {false && (
                <div className="it-upload-area" style={{ marginTop: 10 }}>
                  {/* removed — Rightmove/Zoopla listing link input is deferred post-beta */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="url"
                      className="it-input"
                      placeholder="e.g. https://www.rightmove.co.uk/properties/..."
                      value={listingUrl}
                      onChange={(e) => setListingUrl(e.target.value)}
                      style={{ textTransform: 'none', flex: 1 }}
                      data-testid="listing-url-input"
                    />
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--it-muted)', marginTop: 6 }}>
                    We'll use the listing photos to assess the property condition. The link is not stored.
                  </p>
                  <button
                    className="it-btn it-btn-navy"
                    disabled={!listingUrl.includes('http')}
                    onClick={goScreen3}
                    style={{ marginTop: 10 }}
                    data-testid="url-continue-btn"
                  >
                    Continue <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Upload zone */}
              {photoMode === 'upload' && !aiLoading && !aiSummary && (
                <div className="it-upload-area">
                  <label className="it-photo-drop" htmlFor="photo-input" data-testid="photo-drop-zone">
                    <Upload className="w-6 h-6" style={{ color: 'var(--it-navy)' }} />
                    <p>Tap to browse or drag photos here</p>
                    <small>3-6 photos work best · JPG or PNG</small>
                  </label>
                  <input type="file" id="photo-input" multiple accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
                  
                  {photos.length > 0 && (
                    <>
                      <div className="it-photo-grid">
                        {photos.map((p, i) => (
                          <div key={p.name || i} className="it-photo-thumb">
                            <img src={p.src} alt={p.name} />
                            <button className="it-photo-del" onClick={() => removePhoto(i)}>
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button className="it-btn it-btn-navy" onClick={analysePhotos} data-testid="analyse-photos-btn">
                        Analyse photos and continue <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Analysis Progress */}
              {aiLoading && (
                <div className="it-ai-progress" data-testid="ai-progress">
                  {photos.length > 0 && (
                    <div className="it-photo-grid" style={{ marginBottom: 14 }}>
                      {photos.map((p, i) => (
                        <div key={p.name || i} className="it-photo-thumb"><img src={p.src} alt="" /></div>
                      ))}
                    </div>
                  )}
                  <div className="it-ai-box">
                    <div className="it-ai-dots">
                      <div className="it-ai-dot" /><div className="it-ai-dot" /><div className="it-ai-dot" />
                    </div>
                    <div className="it-ai-witty">{wittyLine}</div>
                  </div>
                </div>
              )}

              {/* Analysis Failed — fall back to manual scope picker (May-2026 user spec).
                  Routes the user back to Screen 1 with manual mode pre-selected. */}
              {analysisFailed && !aiLoading && (
                <div data-testid="analysis-failed-block"
                  style={{
                    background: '#FFF1F2', border: '1.5px solid #FECDD3', borderRadius: 12,
                    padding: '18px 20px', marginTop: 14
                  }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: '#FEE2E2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: 18 }} aria-hidden>⚠️</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#991B1B', marginBottom: 4 }}>
                        We couldn't analyse those photos
                      </div>
                      <div style={{ fontSize: 13, color: '#7F1D1D', lineHeight: 1.55, marginBottom: 12 }}>
                        Photo analysis is in beta and works best with clear interior shots of each room. The only way forward right now is to pick the works that best reflect what you want done from the manual options.
                      </div>
                      <button
                        type="button"
                        className="it-btn it-btn-navy"
                        data-testid="analysis-failed-fallback-btn"
                        onClick={() => {
                          setAnalysisFailed(false);
                          setAiSummary('');
                          setAiDetectedItems([]);
                          setScopeMode('manual');
                          setPhotoMode(null);
                          goScreen(1);
                        }}
                      >
                        Pick from the options instead <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Analysis Result */}
              {aiSummary && !aiLoading && (
                <div className="it-ai-result" data-testid="ai-result">
                  {photos.length > 0 && (
                    <div className="it-photo-grid" style={{ marginBottom: 12 }}>
                      {photos.map((p, i) => (
                        <div key={p.name || i} className="it-photo-thumb"><img src={p.src} alt="" /></div>
                      ))}
                    </div>
                  )}
                  <div className="it-ai-found">
                    <div className="it-ai-found-label">What we found</div>
                    <div className="it-ai-found-text">{aiSummary}</div>
                  </div>
                  <button className="it-btn it-btn-navy" onClick={goScreen3} style={{ marginTop: 12 }} data-testid="continue-2-btn">
                    Review scope and get cost guide <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Skip button */}
              {photoMode === 'skip' && (
                <button className="it-btn it-btn-navy" onClick={goScreen3} style={{ marginTop: 12 }} data-testid="skip-photos-btn">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              )}

              {!photoMode && (
                <div className="it-nav-btns" style={{ marginTop: 16 }}>
                  <button className="it-btn it-btn-ghost" onClick={() => goScreen(1)}>
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── SCREEN 3: Scope confirm ── */}
      {screen === 3 && (
        <div className="it-screen" data-testid="screen-3">
          <div className="it-card">
            <div className="it-card-hdr">
              <h2>Confirm what's included</h2>
              <p>We've suggested what's typical — remove anything that doesn't apply.</p>
            </div>
            <div className="it-card-body">
              <div className="it-scope-list">
                {ALL_SCOPE_ITEMS.map(item => {
                  const isOn = scopeItems.has(item.id);
                  const isAi = aiDetectedItems.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      className={`it-scope-item${isOn ? ' on' : ''}${isAi && isOn ? ' ai-detected' : ''}`}
                      onClick={() => toggleScope(item.id)}
                      data-testid={`scope-${item.id}`}
                    >
                      <div className="it-chk">
                        {isOn && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <div className="it-scope-label">
                          {item.label}
                          {isAi && isOn && <span className="it-ai-tag">From photos</span>}
                        </div>
                        <div className="it-scope-sub">{item.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <button className="it-link-btn" onClick={() => setShowExtras(!showExtras)} style={{ marginTop: 4 }}>
                {showExtras ? '- Hide extras' : '+ Structural changes or stairs'}
              </button>
              {showExtras && (
                <div style={{ marginTop: 6 }}>
                  {EXTRA_SCOPE_ITEMS.map(item => {
                    const isOn = scopeItems.has(item.id);
                    return (
                      <button key={item.id} className={`it-scope-item${isOn ? ' on' : ''}`} onClick={() => toggleScope(item.id)}>
                        <div className="it-chk">{isOn && <Check className="w-3 h-3 text-white" />}</div>
                        <div>
                          <div className="it-scope-label">{item.label}</div>
                          <div className="it-scope-sub">{item.sub}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="it-nav-btns" style={{ marginTop: 16 }}>
                <button className="it-btn it-btn-ghost" onClick={() => goScreen(2)}>
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button className="it-btn it-btn-accent" onClick={goScreen4} data-testid="get-cost-guide-btn">
                  Get my cost guide <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SCREEN 4: Output — State A (gate) → State B (revealed) ── */}
      {screen === 4 && (
        <div className="it-screen" data-testid="screen-4">
          {calculating ? (
            <div className="it-ai-box" style={{ padding: 40, textAlign: 'center' }}>
              <div className="it-ai-dots" style={{ justifyContent: 'center', marginBottom: 16 }}>
                <div className="it-ai-dot" /><div className="it-ai-dot" /><div className="it-ai-dot" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Calculating your estimate...</div>
            </div>
          ) : costResult && (
            <>
              {/* ──────── STATE A: Gate (unauth'd user, estimate hidden) ──────── */}
              {!revealedProjectId ? (
                <div className="it-output-block" data-testid="indicative-state-a">
                  {/* Budget summary — simplified blurred preview before
                      registration. Keep label + blurred range + region chip
                      only; address / size / scope live downstream in SCOPE. */}
                  <div className="it-budget-summary" data-testid="budget-summary-blurred" style={{
                    background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 12,
                    padding: '24px 22px', textAlign: 'center', marginBottom: 14, position: 'relative',
                  }}>
                    <div className="it-out-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: '#64748B', marginBottom: 8 }}>
                      INDICATIVE BUDGET
                    </div>
                    <div
                      className="it-out-range"
                      data-testid="cost-range-blurred"
                      style={{ filter: 'blur(10px)', userSelect: 'none', pointerEvents: 'none', opacity: 0.7, fontSize: 36, fontWeight: 800, color: '#0F2742', lineHeight: 1.1 }}
                    >
                      <small style={{ fontSize: 22 }}>£</small>{formatK(costResult.low)}
                      <small style={{ fontSize: 22 }}> – £</small>{formatK(costResult.high)}k
                    </div>
                    <div className="it-out-range-qualifier" data-testid="cost-range-qualifier" style={{
                      fontSize: 12, color: 'var(--color-muted, #64748B)', fontStyle: 'italic',
                      marginTop: 6,
                    }}>
                      dependent on specification and defined scope
                    </div>
                    {typeof locationFactor === 'number' && (
                      <div
                        data-testid="indicative-region-chip-blurred"
                        style={{
                          display: 'inline-block', marginTop: 14,
                          padding: '5px 12px', borderRadius: 999,
                          background: 'rgba(15, 39, 66, 0.08)', color: '#0F2742',
                          fontSize: 11, fontWeight: 600, letterSpacing: '.01em',
                        }}
                      >
                        Costs adjusted for your region
                      </div>
                    )}
                  </div>

                  {/* Registration / Sign-in gate */}
                  <div className="it-unlock-card" data-testid="registration-gate">
                    <div className="it-unlock-eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Lock className="w-3 h-3" /> {signInMode ? 'Sign in to view it' : 'Create your free account to view it'}
                    </div>
                    <div className="it-unlock-title">Your estimate is ready</div>
                    <div className="it-unlock-sub">{signInMode ? 'Welcome back — enter your details to reveal your estimate.' : 'One step. Email + password. No payment.'}</div>
                    {!signInMode ? (
                      <>
                        {regErr && <div className="it-error" data-testid="reg-error" style={{ background: '#FEF2F2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, border: '1px solid #FECACA' }}>{regErr}</div>}
                        <form onSubmit={handleRegister} data-testid="registration-form">
                          <input
                            type="email" className="it-input"
                            placeholder="Your email address"
                            value={regForm.email}
                            onChange={(e) => setRegForm(f => ({ ...f, email: e.target.value }))}
                            required autoComplete="email"
                            style={{ marginBottom: 10, textTransform: 'none', fontSize: 13 }}
                            data-testid="register-email"
                          />
                          <input
                            type="password" className="it-input"
                            placeholder="Choose a password (minimum 8 characters)"
                            value={regForm.password}
                            onChange={(e) => setRegForm(f => ({ ...f, password: e.target.value }))}
                            required minLength={8} autoComplete="new-password"
                            style={{ marginBottom: 12, textTransform: 'none', fontSize: 13 }}
                            data-testid="register-password"
                          />
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#475569', margin: '0 0 10px', cursor: 'pointer', lineHeight: 1.5 }}>
                            <input type="checkbox" checked={regForm.terms} onChange={(e) => setRegForm(f => ({ ...f, terms: e.target.checked }))} data-testid="register-terms" />
                            <span>I agree to the <button type="button" className="it-link-btn-inline" onClick={() => navigate('/terms')}>Terms and Conditions</button></span>
                          </label>
                          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, color: '#475569', margin: '0 0 16px', cursor: 'pointer', lineHeight: 1.5 }}>
                            <input type="checkbox" checked={regForm.marketing} onChange={(e) => setRegForm(f => ({ ...f, marketing: e.target.checked }))} data-testid="register-marketing" />
                            <span>Help me get the best value on my project — send me contractor rate insights, cost-saving tips and market updates relevant to my refurbishment</span>
                          </label>
                          <button type="submit" className="it-btn it-btn-accent" disabled={regBusy} data-testid="register-submit">
                            {regBusy ? 'Creating account…' : 'See my estimate →'}
                          </button>
                        </form>
                        <div style={{ fontSize: 12, color: '#64748B', textAlign: 'center', marginTop: 14 }}>
                          Already have an account?{' '}
                          <button
                            type="button" className="it-link-btn-inline"
                            onClick={() => { setSignInErr(''); setSignInMode(true); setSignInForm({ email: regForm.email || '', password: '' }); }}
                            data-testid="link-signin-toggle"
                          >
                            Sign in
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        {signInErr && <div className="it-error" data-testid="signin-error" style={{ background: '#FEF2F2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, border: '1px solid #FECACA' }}>{signInErr}</div>}
                        <form onSubmit={handleSignInInline} data-testid="signin-form">
                          <input
                            type="email" className="it-input"
                            placeholder="Your email address"
                            value={signInForm.email}
                            onChange={(e) => setSignInForm(f => ({ ...f, email: e.target.value }))}
                            required autoComplete="email"
                            style={{ marginBottom: 10, textTransform: 'none', fontSize: 13 }}
                            data-testid="signin-email"
                          />
                          <input
                            type="password" className="it-input"
                            placeholder="Password"
                            value={signInForm.password}
                            onChange={(e) => setSignInForm(f => ({ ...f, password: e.target.value }))}
                            required autoComplete="current-password"
                            style={{ marginBottom: 16, textTransform: 'none', fontSize: 13 }}
                            data-testid="signin-password"
                          />
                          <button type="submit" className="it-btn it-btn-accent" disabled={signInBusy} data-testid="signin-submit">
                            {signInBusy ? 'Signing in…' : 'Sign in & see my estimate →'}
                          </button>
                        </form>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#64748B', marginTop: 14, gap: 10, flexWrap: 'wrap' }}>
                          <button
                            type="button" className="it-link-btn-inline"
                            onClick={() => { setRegErr(''); setSignInMode(false); }}
                            data-testid="link-back-to-register"
                          >
                            ← Back to create account
                          </button>
                          <button
                            type="button" className="it-link-btn-inline"
                            onClick={() => navigate('/forgot-password')}
                            data-testid="link-forgot-password"
                          >
                            Forgot password?
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                /* ──────── STATE B: Revealed ──────── */
                <div className="it-output-block" data-testid="indicative-state-b">
                  {/* Budget summary — simplified per Apr 2026 brief.
                      Show only the headline range + region adjustment chip;
                      details (address / size / scope tags) live in the SCOPE
                      flow, not on the marketing-facing reveal screen. */}
                  <div className="it-budget-summary" data-testid="budget-summary" style={{
                    background: '#fff', border: '1.5px solid #E2E8F0', borderRadius: 12,
                    padding: '24px 22px', textAlign: 'center', marginBottom: 14,
                  }}>
                    <div className="it-out-label" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: '#64748B', marginBottom: 8 }}>
                      INDICATIVE BUDGET
                    </div>
                    <div className="it-out-range" data-testid="cost-range" style={{ fontSize: 36, fontWeight: 800, color: '#0F2742', lineHeight: 1.1 }}>
                      <small style={{ fontSize: 22 }}>£</small>{formatK(costResult.low)}
                      <small style={{ fontSize: 22 }}> – £</small>{formatK(costResult.high)}k
                    </div>
                    <div className="it-out-range-qualifier" data-testid="cost-range-qualifier-b" style={{
                      fontSize: 12, color: 'var(--color-muted, #64748B)', fontStyle: 'italic',
                      marginTop: 6,
                    }}>
                      dependent on specification and defined scope
                    </div>
                    {typeof locationFactor === 'number' && (
                      <div
                        data-testid="indicative-region-chip"
                        style={{
                          display: 'inline-block', marginTop: 14,
                          padding: '5px 12px', borderRadius: 999,
                          background: 'rgba(15, 39, 66, 0.08)', color: '#0F2742',
                          fontSize: 11, fontWeight: 600, letterSpacing: '.01em',
                        }}
                      >
                        Costs adjusted for your region
                      </div>
                    )}
                  </div>

                  {/* What's included / not included — May 2026 transparency
                      panel. Sourced from costResult.inclusions (backend). The
                      upsell jumps the user back to Screen 1 with a different
                      refurb_level pre-selected so they can compare. */}
                  {costResult.inclusions && (
                    <ScopeInclusionsPanel
                      inclusions={costResult.inclusions}
                      onUpsell={(scopeId) => {
                        // Switch the refurb selection and re-calculate; stay on
                        // Screen 4 so the user immediately sees the new price.
                        // Note: we deliberately do NOT clear revealedProjectId —
                        // doing so re-triggers the auto-reveal useEffect with
                        // stale state and races our setCostResult update.
                        setRefurbLevel(scopeId);
                        (async () => {
                          try {
                            const area = epcArea || Number(areaOverride) || 80;
                            const res = await axios.post(`${API}/indicative/calculate`, {
                              area_m2: area,
                              scope_level: scopeId,
                              postcode: postcode,
                              scope_items: [],
                              property_type: propType,
                            });
                            setCostResult(res.data);
                          } catch (err) {
                            console.error('Upsell recalculation failed:', err);
                          }
                        })();
                      }}
                    />
                  )}

                  {/* SCOPE Assessment card — Claude-powered property summary
                      to build buyer confidence before the SCOPE upsell.
                      May-2026 user spec. Free during launch (badge shown). */}
                  <div data-testid="upsell-scope-assessment" style={{ marginBottom: 16 }}>
                    <ScopeAssessmentCard
                      payload={{
                        address: addressFull || address || '',
                        region: locationFactor?.region || '',
                        property_type: propType,
                        refurb_level: refurbLevel,
                        gia_m2: epcArea || Number(areaOverride) || 80,
                        totals: { low: costResult.low, high: costResult.high },
                        rooms: [],
                      }}
                    />
                  </div>

                  {/* ── Primary upsell: SCOPE — refresh per Apr 2026 brief.
                      No price on the upsell text; emphasise the 10-minute
                      promise and the 3 deliverables a buyer can pick from. */}
                  <div className="it-unlock-card" data-testid="upsell-scope">
                    <div className="it-unlock-title">Refine your estimate to be more definitive with SCOPE in as little as 10 minutes.</div>
                    <div className="it-unlock-sub">
                      In 10 minutes you can have a refined estimate, a key materials schedule, or a full schedule of works for builders to price.
                    </div>
                    <button
                      className="it-btn it-btn-accent"
                      onClick={() => {
                        /* §3.6 / §3.9 + Feb 2026 brief — persist EVERYTHING we already have into SCOPE
                           so the Project Info step is pre-filled. Adds area_m2 + property_type + scope
                           tier so the chat can skip the redundant questions. */
                        const params = new URLSearchParams();
                        if (addressFull) params.set('address', addressFull);
                        else if (address) params.set('address', address);
                        if (postcode) params.set('postcode', postcode);
                        if (propType) params.set('property_type', propType);
                        if (refurbLevel) params.set('scale_of_works', refurbLevel);
                        const area = epcArea || Number(areaOverride);
                        if (area && area > 0) params.set('area_m2', String(Math.round(area)));
                        if (region) params.set('region', region);
                        if (revealedProjectId) params.set('from_indicative', revealedProjectId);
                        const qs = params.toString();
                        navigate(`/scope${qs ? `?${qs}` : ''}`);
                      }}
                      data-testid="upsell-scope-btn"
                      style={{ marginTop: 18 }}
                    >
                      Continue with SCOPE →
                    </button>
                  </div>

                  {/* ── QS Consultation Call (the QS Check upsell card was
                      removed per Apr-2026 brief — clients are routed to the
                      consultation call instead). ── */}
                  <div style={{ marginTop: 14 }} data-testid="upsell-call">
                    <QSCallCTA
                      variant="card"
                      showRedeem
                      testid="qs-call-cta-indicative"
                      userEmail=""
                    />
                  </div>

                  {/* Ref + Dashboard link */}
                  {revealedOrderRef && (
                    <div style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginTop: 18 }} data-testid="order-ref">
                      Saved to your account as <strong>{revealedOrderRef}</strong> · <button type="button" className="it-link-btn-inline" onClick={() => navigate('/account')}>View in My Account →</button>
                    </div>
                  )}
                </div>
              )}

              {/* Disclaimer — always visible */}
              <div className="it-disclaimer" data-testid="disclaimer">
                <strong>Indicative estimate only — not a quote.</strong> Figures are built from real project data and refined using current market conditions and location. Actual costs will vary depending on property condition, specification and contractor pricing. This is not a contract document and must not be used for formal tendering or JCT purposes. Full methodology and limitations are set out in our <button className="it-link-btn-inline" onClick={() => navigate('/terms')}>Terms and Conditions</button>. For projects requiring professional QS sign-off, contact <strong>Macventure Consultancy</strong>.
              </div>
            </>
          )}
        </div>
      )}

      {/* ── SCREEN 5: Managed Service Handoff ── */}
      {screen === 'managed' && (
        <div className="it-screen" data-testid="screen-managed">
          <div className="it-managed-hero">
            <div className="it-managed-eyebrow">Project type detected</div>
            <h2>This project needs professional QS input</h2>
            <p>Extensions, loft conversions, new builds, listed buildings and HMO conversions involve structural, planning and building regulations considerations that go beyond what this tool covers.</p>
          </div>

          <div className="it-card" style={{ marginBottom: 14 }}>
            <div className="it-card-body">
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--it-navy)', marginBottom: 12 }}>Why this type of project needs a different approach</div>
              {[
                { icon: '⚖️', title: 'JCT-level contracts', desc: 'Projects of this scale and complexity typically require formal JCT building contracts.' },
                { icon: '🏛️', title: 'Statutory consents', desc: 'Building regulations full plans approval, planning permission and party wall agreements require professional coordination.' },
                { icon: '📐', title: 'Structural complexity', desc: 'These projects involve structural engineer coordination and specialist procurement.' },
                { icon: '💰', title: 'Cost exposure', desc: 'Projects of this type regularly exceed £100k. An automated estimate is not appropriate — a qualified Quantity Surveyor is required.' },
              ].map(r => (
                <div key={r.title} className="it-managed-reason">
                  <div className="it-managed-reason-icon">{r.icon}</div>
                  <div><strong style={{ color: 'var(--it-navy)' }}>{r.title}</strong> — {r.desc}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="it-managed-cta-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
              <img src="/macventure-logo.png" alt="Macventure Consultancy" className="it-macventure-logo" />
              <div>
                <div className="it-managed-cta-eyebrow">Chartered QS support</div>
                <div className="it-managed-cta-title">Macventure Consultancy</div>
                <div className="it-managed-cta-desc">A chartered quantity surveying consultancy providing professional QS services for residential and commercial projects across the UK.</div>
              </div>
            </div>
            <button className="it-btn it-btn-accent" onClick={() => navigate('/upload')} data-testid="managed-cta-btn">
              Submit your project to Macventure Consultancy <ArrowRight className="w-4 h-4" />
            </button>
            <div style={{ fontSize: 10, color: 'var(--it-muted)', textAlign: 'center', marginTop: 8 }}>
              Via the Schedules of Work managed service · Professional QS · Bespoke pricing
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button className="it-link-btn" onClick={() => { setRefurbLevel(''); goScreen(1); }} data-testid="back-from-managed">
              <ArrowLeft className="w-3 h-3" /> Actually, my project is a straightforward refurb
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default IndicativeTool;
